import React, { useState, useEffect } from "react";
import {
  Square,
  Triangle,
  Circle,
  Hexagon,
  RotateCcw,
  ArrowUpRight,
  Cpu,
  Gamepad2,
  Terminal,
  Sparkles,
} from "lucide-react";

const BlueprintPortfolio = ({ filteredTools = [] }) => {
  // --- CORE STATE ---
  const [activeModule, setActiveModule] = useState(null); // 'memory', 'color', null
  const [gameState, setGameState] = useState("idle"); // idle, showing, playing, won, failed
  const [score, setScore] = useState(0);
  const [logs, setLogs] = useState([
    "Welcome to the Pro Hub.",
    "Choose a challenge to start!",
  ]);

  // Game 1: Memory Recall
  const [sequence, setSequence] = useState([]);
  const [userSequence, setUserSequence] = useState([]);
  const [activeButton, setActiveButton] = useState(null);

  // Game 2: Color Match (Interference Mode)
  const [targetColor, setTargetColor] = useState("");
  const [displayTextClass, setDisplayTextClass] = useState("");
  const [options, setOptions] = useState([]);
  const [glitchButton, setGlitchButton] = useState(null);

  const buttons = [
    { id: 1, icon: <Square />, iconName: "Square", label: "Triangle" },
    { id: 2, icon: <Triangle />, iconName: "Triangle", label: "Circle" },
    { id: 3, icon: <Circle />, iconName: "Circle", label: "Hexagon" },
    { id: 4, icon: <Hexagon />, iconName: "Hexagon", label: "Square" },
  ];

  const colorData = [
    { name: "Red", bg: "bg-red-500", text: "text-red-500" },
    { name: "Blue", bg: "bg-blue-500", text: "text-blue-500" },
    { name: "Green", bg: "bg-green-500", text: "text-green-500" },
    { name: "Yellow", bg: "bg-yellow-400", text: "text-yellow-400" },
  ];

  const addLog = (msg) => {
    setLogs((prev) => [msg, ...prev].slice(0, 4));
  };

  // --- GAME 1: MEMORY RECALL LOGIC ---
  const playMemorySequence = (seq) => {
    setGameState("showing");

    seq.forEach((id, index) => {
      setTimeout(
        () => {
          setActiveButton(id);
          setGlitchButton(id); // <--- Add this

          setTimeout(() => {
            setActiveButton(null);
            setGlitchButton(null); // <--- Add this
          }, 600);
        },
        (index + 1) * 1000,
      );
    });

    setTimeout(
      () => {
        setGameState("playing");
      },
      (seq.length + 1) * 1000,
    );
  };

  const startNextMemoryLevel = () => {
    const nextStep = Math.floor(Math.random() * 4) + 1;
    const newSequence = [...sequence, nextStep];
    setSequence(newSequence);
    setUserSequence([]);
    playMemorySequence(newSequence);
  };

  const handleMemoryClick = (id) => {
    if (gameState !== "playing") return;

    // Find the button that was FLASHED
    const flashedButtonId = sequence[userSequence.length];
    const flashedButton = buttons.find((b) => b.id === flashedButtonId);

    // Find the button the user just CLICKED
    const clickedButton = buttons.find((b) => b.id === id);

    // Safety Check: If either button isn't found, exit early to prevent the crash
    if (!flashedButton || !clickedButton) {
      console.error("Button data missing");
      return;
    }

    // THE TRAP LOGIC: Compare clicked label vs flashed icon identity
    if (clickedButton.label !== flashedButton.iconName) {
      setGameState("failed");
      addLog("Cognitive Mismatch: Wrong Label selected.");
      return;
    }

    // ... rest of your success logic (Score, Next Level, etc.)
    const nextUserSeq = [...userSequence, id];
    setUserSequence(nextUserSeq);

    if (nextUserSeq.length === sequence.length) {
      if (score + 1 >= 4) {
        setGameState("won");
      } else {
        setScore(score + 1);
        setTimeout(() => startNextMemoryLevel(), 600);
      }
    }
  };

  // --- GAME 2: COLOR MATCH (STROP EFFECT) LOGIC ---
  const generateColorRound = () => {
    const target = colorData[Math.floor(Math.random() * colorData.length)];
    // Pick a DIFFERENT color for the text to confuse the player
    const distractions = colorData.filter((c) => c.name !== target.name);
    const fakeColor =
      distractions[Math.floor(Math.random() * distractions.length)];

    setTargetColor(target.name);
    setDisplayTextClass(fakeColor.text);
    setOptions([...colorData].sort(() => Math.random() - 0.5));
    setGameState("playing");
  };

  const handleColorClick = (colorName) => {
    if (colorName === targetColor) {
      addLog(`Correct! It was ${colorName}`);
      if (score + 1 >= 6) {
        setGameState("won");
        addLog("Sharp mind! Access granted.");
      } else {
        setScore(score + 1);
        generateColorRound();
      }
    } else {
      setGameState("failed");
      addLog("Tricked you! Focus on the word.");
    }
  };

  const initGame = (type) => {
    setActiveModule(type);
    setScore(0);
    if (type === "memory") {
      const first = Math.floor(Math.random() * 4) + 1;
      setSequence([first]);
      playMemorySequence([first]);
    } else {
      addLog("Read the WORD, ignore the color!");
      generateColorRound();
    }
  };

  const restartGame = () => {
    setScore(0);
    setUserSequence([]);
    setGameState("idle"); // or call your start function directly
    addLog("System rebooting... Try again.");
    // Trigger your initial level start here
    startNextMemoryLevel();
  };

  return (
    <div className="bg-white font-sans text-slate-900 overflow-x-hidden">
      {/* HEADER SECTION */}
      <header
        className={`relative w-full min-h-[90vh] md:min-h-screen flex flex-col transition-colors duration-500 border-b-8 md:border-b-[16px] border-slate-900 ${gameState === "won" ? "bg-green-50" : "bg-white"}`}
      >
        {/* Subtle Grid Background */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
            backgroundSize: "30px 30px",
          }}
        ></div>

        <div className="relative z-10 flex-1 flex flex-col p-4 md:p-12 max-w-6xl mx-auto w-full">
          {/* Top Nav */}
          <div className="flex flex-row justify-between items-center border-b-2 md:border-b-[4px] border-slate-900 pb-2 mb-6 md:mb-10">
            {/* Left: The Brand Signature */}
<div className="flex items-center gap-4">
  {/* BRAND LOGO */}
  <div className="relative w-10 h-10 flex items-center justify-center group cursor-pointer">
    {/* Sharp Industrial Frame */}
    <div className="absolute inset-0 border-2 border-slate-900 rounded-lg group-hover:border-cyan-600 group-hover:rotate-90 transition-all duration-500" />
    
    {/* Inner "M+" */}
    <span className="text-slate-900 font-black text-base relative tracking-tighter group-hover:text-cyan-600 transition-colors">
      M<span className="text-cyan-600">+</span>
    </span>
  </div>

  {/* BRAND TEXT */}
  <div className="flex flex-col justify-center">
    <h1 className="font-black text-2xl md:text-3xl tracking-tighter text-slate-900 uppercase leading-none">
      More<span className="text-cyan-600">Tools</span>
    </h1>
    
    {/* Technical Accent Line (Replaces the signature name) */}
    <div className="flex items-center gap-1 mt-1">
      <div className="h-[2px] w-8 bg-cyan-600 rounded-full" />
      <div className="h-[2px] w-2 bg-slate-200 rounded-full" />
      <div className="h-[2px] w-1 bg-slate-200 rounded-full" />
    </div>
  </div>
</div>

            {/* Right: The Attribution */}
            <div className="flex items-center gap-2">
              <span className="text-[8px] md:text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                By
              </span>
              <span className="font-black text-[11px] md:text-lg uppercase tracking-tighter text-slate-900 border-l-2 border-blue-500 pl-2">
                Omkar More
              </span>
            </div>
          </div>

          {/* MAIN GAME AREA */}
          <div className="flex-1 flex flex-col justify-start items-center pt-10 md:pt-20 pb-12">
            {/* START SCREEN */}
            {gameState === "idle" && (
              <div className="w-full max-w-3xl space-y-8 animate-in fade-in slide-in-from-bottom-4">
                <div className="text-center space-y-2 md:space-y-4">
                  <h1 className="text-4xl md:text-8xl font-black tracking-tighter uppercase leading-none">
                    Unlock Apps
                  </h1>
                  <p className="text-slate-500 font-bold uppercase text-xs md:text-sm tracking-widest">
                    Complete a quick task to access our full library of free
                    online tools
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
                  <button
                    onClick={() => initGame("memory")}
                    className="group p-6 md:p-10 border-4 border-slate-900 bg-white hover:bg-slate-900 hover:text-white transition-all text-left shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
                  >
                    <Gamepad2 className="mb-4 w-8 h-8 md:w-12 md:h-12" />
                    <h3 className="text-2xl md:text-3xl font-black uppercase">
                      Memory Match
                    </h3>
                    <p className="text-[10px] md:text-xs font-bold opacity-60 uppercase mt-2">
                      Follow the pattern of shapes
                    </p>
                  </button>

                  <button
                    onClick={() => initGame("logic")}
                    className="group p-6 md:p-10 border-4 border-slate-900 bg-white hover:bg-slate-900 hover:text-white transition-all text-left shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
                  >
                    <Cpu className="mb-4 w-8 h-8 md:w-12 md:h-12" />
                    <h3 className="text-2xl md:text-3xl font-black uppercase">
                      Color Focus
                    </h3>
                    <p className="text-[10px] md:text-xs font-bold opacity-60 uppercase mt-2">
                      Read the words, ignore the colors
                    </p>
                  </button>
                </div>
              </div>
            )}


            {/* MODULE 01: THE NEURAL OVERRIDE */}
    {activeModule === "memory" &&
    (gameState === "showing" ||
        gameState === "playing" ||
        gameState === "failed") && (
        /* Reduced max-width from xl to lg for a tighter desktop look */
        <div className="w-full max-w-sm md:max-w-lg px-4 space-y-5 md:space-y-6 text-center animate-in zoom-in duration-300">
        
        {/* Simplified Header */}
        <div className="space-y-2 md:space-y-3">
            <div className="flex justify-between items-center px-1">
            <div className="text-left">
                <h2 className="font-black uppercase tracking-tight text-lg md:text-xl text-slate-900 leading-none">
                Memory Test
                </h2>
                <p className="text-[10px] md:text-[11px] font-bold text-slate-400 uppercase tracking-widest">
                {gameState === "failed" ? "Link Broken" : "Follow the pattern"}
                </p>
            </div>

            <div className="text-right">
                <span className="text-xs font-black bg-slate-900 text-white px-2 py-1 rounded-sm">
                LEVEL {score + 1}/4
                </span>
            </div>
            </div>

            {/* Progress Bar - keeping it slim */}
            <div className="relative w-full h-3 md:h-4 border-[3px] border-slate-900 bg-slate-100 overflow-hidden">
            <div
                className="h-full bg-slate-900 transition-all duration-700 ease-out"
                style={{ width: `${(score / 4) * 100}%` }}
            />
            <div className="absolute inset-0 opacity-10 pointer-events-none bg-[linear-gradient(to_right,#000_1px,transparent_1px)] bg-[size:25%_100%]" />
            </div>
        </div>

        {/* The Mismatch Grid - Slightly smaller gaps and padding */}
        <div className="grid grid-cols-2 gap-4 md:gap-6 relative">
            {buttons.map((btn) => (
            <button
                key={btn.id}
                disabled={gameState === "showing"}
                onClick={() =>
                gameState === "failed" ? restartGame() : handleMemoryClick(btn.id)
                }
                className={`aspect-square border-[3px] md:border-4 flex flex-col items-center justify-center p-4 md:p-6 transition-all relative group overflow-hidden ${
                activeButton === btn.id
                    ? "bg-slate-900 text-white scale-95 border-slate-900 shadow-none"
                    : "bg-white border-slate-900 shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] md:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] active:translate-x-1 active:translate-y-1 active:shadow-none"
                }`}
            >
                <div className="relative z-10 flex flex-col items-center gap-2 md:gap-4 w-full">
                <div
                    className={`transition-transform duration-300 ${activeButton === btn.id ? "scale-110" : "text-slate-900"}`}
                >
                    {/* Moderated icon sizes */}
                    {React.cloneElement(btn.icon, {
                    className: "w-10 h-10 md:w-16 md:h-16",
                    })}
                </div>

                <div
                    className={`w-full py-1 md:py-1.5 border-2 font-black uppercase text-[10px] md:text-[14px] ${
                    activeButton === btn.id
                        ? "border-white bg-white text-slate-900"
                        : "border-slate-900 bg-slate-900 text-white"
                    }`}
                >
                    {btn.label}
                </div>
                </div>

                {/* FLASH OVERLAY */}
                {glitchButton === btn.id && (
                <div className="absolute inset-0 bg-slate-900 flex items-center justify-center z-20">
                    <div className="text-white scale-[2] md:scale-[3] animate-pulse">
                    {btn.icon}
                    </div>
                </div>
                )}
            </button>
            ))}

            {/* FAIL OVERLAY */}
            {gameState === "failed" && (
            <div
                onClick={restartGame}
                className="absolute inset-0 z-30 bg-slate-900/90 backdrop-blur-[2px] flex flex-col items-center justify-center cursor-pointer animate-in fade-in zoom-in duration-200"
            >
                <div className="bg-white text-slate-900 font-black px-4 py-2 md:px-6 md:py-3 text-lg md:text-2xl tracking-tighter mb-4 border-4 border-slate-900 shadow-[4px_4px_0_0_#ef4444]">
                WRONG CHOICE
                </div>
                <p className="text-white font-bold text-[9px] md:text-[11px] uppercase tracking-[0.3em] animate-pulse">
                Click anywhere to retry
                </p>
            </div>
            )}
        </div>

        {/* Instruction Box */}
        <div className="bg-slate-900 text-white p-3 md:p-5 rounded-sm text-left flex items-center gap-4">
            <div className="h-6 md:h-10 w-1 bg-blue-400" />
            <div>
            <p className="font-black text-[10px] md:text-xs uppercase tracking-wider text-blue-400">
                Current Task:
            </p>
            <p className="font-bold text-[12px] md:text-[15px] leading-tight">
                {gameState === "showing" && "Watch the shapes carefully."}
                {gameState === "playing" && "Click the NAME of the shape you saw."}
                {gameState === "failed" && "Game over. Tap to try again."}
            </p>
            </div>
        </div>
        </div>
    )}

            {/* GAME 2 UI */}
{activeModule === "logic" && (
  <div className="w-full max-w-md px-4 space-y-10 text-center animate-in zoom-in">
    
    {/* 1. THE LOADER - Added a "Syncing" message for better UX during the wait */}
    {gameState === "loading" && (
      <div className="flex flex-col items-center justify-center py-20 animate-in fade-in duration-300">
        <div className="relative w-20 h-20 md:w-24 md:h-24">
          <div className="absolute inset-0 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
          <div className="absolute inset-4 bg-slate-900 rounded-full animate-pulse" />
        </div>
        
        <div className="mt-8 space-y-2">
          <h3 className="font-black uppercase tracking-[0.3em] text-slate-900 text-sm">
            Loading Game
          </h3>
          <p className="text-[10px] font-bold text-slate-400 animate-pulse uppercase">
           Please wait...
          </p>
        </div>
      </div>
    )}

    {/* 2. PLAYING STATE */}
    {gameState === "playing" && (
      <div className="space-y-10 animate-in fade-in slide-in-from-top-4 duration-500">
        <div className="space-y-4">
          <span className="bg-red-500 text-white text-[10px] font-black px-4 py-1 uppercase tracking-widest">
            Mind Trap!
          </span>
          <div
            className={`text-6xl md:text-9xl font-black uppercase tracking-tighter italic border-y-4 border-slate-100 py-6 ${displayTextClass}`}
          >
            {targetColor}
          </div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Click the color named above
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {options.map((opt, i) => (
            <button
              key={i}
              onClick={() => handleColorClick(opt.name)}
              className={`h-20 md:h-28 border-4 border-slate-900 ${opt.bg} shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-1 active:translate-y-1 transition-all`}
            />
          ))}
        </div>
      </div>
    )}

    {/* 3. FAILED STATE */}
    {gameState === "failed" && (
      <div className="space-y-8 animate-in fade-in zoom-in duration-200">
        <div className="space-y-4">
  {/* Clear, bold header for immediate understanding */}
  <div className="space-y-1">
    <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-red-600 leading-none drop-shadow-sm">
      GAME OVER
    </h2>
    <p className="text-xs md:text-sm font-black text-slate-900 uppercase tracking-widest">
      That wasn't the right one!
    </p>
  </div>

  {/* Sub-text to explain the mistake simply */}
  <div className="bg-slate-100 py-2 px-4 inline-block border-l-4 border-slate-900">
    <p className="text-[10px] md:text-xs font-bold text-slate-500 uppercase tracking-wider">
      The colors switched — try to focus on the word!
    </p>
  </div>
</div>

        <div className="space-y-4">
          <button
            onClick={() => {
              // STEP 1: Set to loading immediately
              setGameState('loading');
              
              // STEP 2: Use a small delay to reset the score and trigger the logic
              // We use a slight delay so the UI doesn't "snap" too fast
              setTimeout(() => {
                setScore(0);
                restartGame(); 
                
                // STEP 3: Force the state to 'playing' if restartGame() has internal delays
                // This ensures the loader disappears ONLY when we are ready
                setGameState('playing'); 
              }, 600); 
            }}
            className="w-full py-6 border-4 border-slate-900 bg-white hover:bg-slate-900 hover:text-white transition-all font-black uppercase text-2xl shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-1 active:translate-y-1"
          >
            Restart_Test
          </button>
          
          <button
            onClick={() => {
              setGameState('idle');
              setActiveModule(null);
            }}
            className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 hover:text-red-500 transition-colors"
          >
            Terminate Session
          </button>
        </div>
      </div>
    )}
  </div>
)}
            <br></br>

            {/* FAIL STATE */}
            {gameState === "failed" && (
              <div className="text-center space-y-6 animate-in shake px-4">
                <button
                  onClick={() => setGameState("idle")}
                  className="w-full md:w-auto border-4 border-slate-900 px-12 py-4 font-black uppercase hover:bg-slate-900 hover:text-white shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] transition-all"
                >
                  Back to Hub
                </button>
              </div>
            )}

            {/* WIN STATE */}
            {gameState === "won" && (
              <div className="w-full max-w-3xl px-4 space-y-8 animate-in slide-in-from-bottom-10">
                <div className="border-l-[12px] md:border-l-[20px] border-slate-900 pl-6 md:pl-10">
                  <h1 className="text-5xl md:text-[120px] font-black uppercase leading-[0.85] tracking-tighter">
                    Ready.
                    <br />
                    To Go.
                  </h1>
                  <p className="text-slate-500 mt-4 md:mt-8 font-black uppercase text-sm md:text-xl tracking-widest">
                    Challenges passed. Tools unlocked below.
                  </p>
                </div>
                <div className="flex items-center gap-4 animate-bounce text-slate-400">
                  <div className="w-1 h-12 bg-slate-900"></div>
                  <span className="text-[10px] font-black uppercase tracking-widest">
                    Scroll down
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Simple Log Footer */}
          <div className="mt-auto pt-6 border-t-2 border-slate-100 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="font-mono text-[9px] md:text-[11px] text-slate-400 uppercase tracking-widest">
              {logs[0]}
            </div>
            <div className="hidden md:flex gap-4 font-black text-[10px] text-slate-300 uppercase">
              <span>Ver: 2.0.4</span>
              <span>Status: Active</span>
            </div>
          </div>
        </div>
      </header>

 
    </div>
  );
};

export default BlueprintPortfolio;
